/**
 * Floating field navigator pinned to the bottom of the signing canvas — the
 * dark pill from the "Signing View" frame of `Signer Experience.dc.html`. It
 * walks the signer through their own fields (previous / next) and mirrors the
 * focused field's label, position and filled state.
 */
import { Box, ButtonBase, Typography } from "@mui/material";
import tokens from "../SignerExperience.tokens";
import { ChevronLeftIcon, NextChevronIcon } from "./SignerExperienceIcons";

const { palette, fieldNav } = tokens;

interface FieldNavigatorProps {
  /** Human label for the focused field, e.g. "Signature". */
  label: string;
  /** 1-based position of the focused field within the signer's own fields. */
  position: number;
  total: number;
  /** Focused field already carries a value. */
  filled: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

const FieldNavigator = ({
  label,
  position,
  total,
  filled,
  onPrevious,
  onNext,
}: FieldNavigatorProps) => (
  <Box
    component="nav"
    aria-label="Field navigation"
    sx={{
      position: "absolute",
      left: "50%",
      bottom: fieldNav.bottom,
      transform: "translateX(-50%)",
      display: "flex",
      alignItems: "center",
      gap: fieldNav.gap,
      backgroundColor: fieldNav.background,
      borderRadius: fieldNav.radius,
      p: fieldNav.padding,
      boxShadow: fieldNav.shadow,
      zIndex: 30,
    }}
  >
    <ButtonBase
      aria-label="Previous field"
      title="Previous field"
      onClick={onPrevious}
      sx={{
        width: fieldNav.arrowSize,
        height: fieldNav.arrowSize,
        borderRadius: "50%",
        backgroundColor: fieldNav.arrowBackground,
        color: palette.white,
        "&:hover": { backgroundColor: fieldNav.arrowHoverBackground },
        "&.Mui-focusVisible": {
          outline: `2px solid ${palette.white}`,
          outlineOffset: "2px",
        },
      }}
    >
      <ChevronLeftIcon size={fieldNav.arrowIconSize} />
    </ButtonBase>

    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: fieldNav.labelGap,
        px: fieldNav.labelPaddingX,
        minWidth: fieldNav.labelMinWidth,
        justifyContent: "center",
      }}
    >
      <Box
        role="img"
        aria-label={filled ? "Completed" : "Not yet filled"}
        sx={{
          width: fieldNav.dotSize,
          height: fieldNav.dotSize,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: filled ? palette.dotDone : palette.dotTodo,
        }}
      />
      <Typography
        sx={{
          fontSize: fieldNav.labelFontSize,
          fontWeight: fieldNav.labelFontWeight,
          color: palette.white,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: fieldNav.posFontSize,
          fontWeight: 600,
          color: fieldNav.posColor,
          whiteSpace: "nowrap",
        }}
      >
        {position} of {total}
      </Typography>
    </Box>

    <ButtonBase
      aria-label="Next field"
      title="Next field"
      onClick={onNext}
      sx={{
        height: fieldNav.nextHeight,
        px: fieldNav.nextPaddingX,
        borderRadius: fieldNav.nextRadius,
        // Same brand red outside an embed; follows the partner's colour inside one.
        backgroundColor: (t) => t.palette.signer.orange.main,
        color: palette.white,
        fontFamily: "inherit",
        fontSize: fieldNav.nextFontSize,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        gap: "6px",
        "&:hover": { backgroundColor: (t) => t.palette.signer.orange.dark },
        "&.Mui-focusVisible": {
          outline: `2px solid ${palette.white}`,
          outlineOffset: "2px",
        },
      }}
    >
      Next
      <NextChevronIcon size={fieldNav.nextIconSize} />
    </ButtonBase>
  </Box>
);

export default FieldNavigator;
